import React, { useState, useCallback, useEffect } from 'react';
import { TableHead, TableBody, Button, InputWithSearch } from '../shared';
import {
  getLocalStorageItem,
  popup,
  sortingByDate,
  sortingByName,
  sortingBySize,
  sortingByValue,
} from '../../utils';
import { BUTTON, LOCAL_STORAGE, POPUP_TYPE, SORTING } from '../../constants';
import { RightSplitTable } from '../common/RightSplitTable';
import { SearchBar } from './SearchBar';
import { DropDown } from './DropDown';

export const PaginatedTable = ({
  columns,
  initialData = [],
  PAGE_SIZE = 20,
  colFilter,
  to,
  compiler,
  popUp,
  setPopUp,
  setPopUpData,
  setReload,
  func,
  className = 'table-1',
  setPageNumber,
  paginationData,
  tooltip = true,
  showRightTable,
  fullProductData,
  rightTableLoading,
  isLoading,
  setUpdatedMoves,
  rightTableColumn,
  showCollapseTable,
  collapseTableDataFunction,
  // Optional search props
  showInlineSearch = false,
  search,
  setSearch,
  searchTerm,
  setSearchTerm,
  showFilter,
  showGroupBy,
  sort1,
  setSort1,
  sortingArray1,
  onDefaultSearch,
  filterFieldName,
  resetFilter,
  groupArray,
  groupBy,
  handleGroupByChange,
  availableValuesGroupBy,
  setAvailableValuesGroupBy,
  selectedItem,
  setSelectedItem,
  removeGroupBy,
  // readyStatusFilter,
  // setReadyStatusFilter
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [sortInfo, setSortInfo] = useState({
    sortType: SORTING.DEFAULT,
    index: 0,
    valueType: '',
    value: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [jumpPage, setJumpPage] = useState(''); // State for input box
  const [vendor_type] = useState(() => getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE));

  const finalColumns = columns.filter((column) => selectedColumns.includes(column.name));
  // Initialize data on component mount or when initialData changes
  useEffect(() => {
    setData(Array.isArray(initialData) ? initialData : []);
    setCurrentPage(1); // Reset to the first page on data change
  }, [initialData]);

  // Update paginated data when data or currentPage changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    setPaginatedData(data.slice(startIndex, endIndex));
  }, [data, currentPage, PAGE_SIZE]);

  useEffect(() => {
    // Update only if selectedColumns hasn't been set
    setSelectedColumns(current => current.length ? current : columns.map((col) => col.name));
  }, [columns]);

  // Sorting handler function
  const handleSort = useCallback(
    ({ sortInfo, data }) => {
      const { sortType, index, valueType } = sortInfo;
      let result = [];
      const dataFieldName = columns[index]?.dataField;
      const zeroFirst = columns[index]?.zeroFirst || false;

      if (valueType === 'String') {
        result = sortingByName({
          data: data,
          sortBy: dataFieldName,
          order: sortType === SORTING.ASCENDING ? 1 : 0,
        });
      } else if (valueType === 'Number' || valueType === 'Integer' || (valueType === 'Size' && vendor_type === 'fabric')) {
        result = sortingByValue({
          data: data,
          sortBy: dataFieldName,
          order: sortType === SORTING.ASCENDING ? 0 : 1,
          zeroFirst: zeroFirst,
        });
      } else if (valueType === 'Size' && !['fabric', 'yarn'].includes(vendor_type)) {
        result = sortingBySize({
          data: data,
          sortBy: dataFieldName,
          order: sortType === SORTING.ASCENDING ? 0 : 1,
        });
      } else {
        result = sortingByDate({
          data: data,
          sortBy: dataFieldName,
          order: sortType === SORTING.ASCENDING ? 0 : 1,
        });
      }
      return result;
    },
    [columns, vendor_type],
  );

  // Pagination logic for displayed page numbers
  // const getDisplayedPages = () => {
  //   const totalPages = Math.ceil(paginationData.total_records / PAGE_SIZE);
  //   const currentPage = paginationData.page;
  //   const pages = [];

  //   if (currentPage > 1) pages.push(currentPage - 1);
  //   pages.push(currentPage);
  //   if (currentPage < totalPages) pages.push(currentPage + 1);

  //   // Ensure two pages ahead for the first page
  //   if (currentPage === 1) {
  //     if (currentPage + 2 <= totalPages) pages.push(currentPage + 2);
  //   }

  //   // Ensure two pages behind for the last page
  //   if (currentPage === totalPages) {
  //     if (currentPage - 2 > 0) pages.unshift(currentPage - 2);
  //   }

  //   return pages;
  // };

  // Handle page jump input
  const handleJumpToPage = () => {
    const totalPages = Math.ceil(paginationData.total_records / PAGE_SIZE);
    const targetPage = parseInt(jumpPage, 10);
    if (targetPage >= 1 && targetPage <= totalPages) {
      setPageNumber(targetPage);
    } else {
      popup(POPUP_TYPE.ERR, `Please enter a valid page number between 1 and ${totalPages}.`);
    }
  };

  return (
    <>
      {/* Inline search bar only when showInlineSearch is true */}
      {showInlineSearch && search !== undefined && setSearch && (
        <>
          <div className="d-flex gap-2 align-items-center mb-3" style={{ maxWidth: '550px' }}>
            <SearchBar
              search={search}
              setSearch={setSearch}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onDefaultSearch={onDefaultSearch}
              inline={true}
            />
            {showFilter && (
              <>
                <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
                {sort1 !== filterFieldName && (
                  <Button
                    clickHandler={resetFilter}
                    className="blue-btn d-flex align-items-center justify-content-center px-3">
                    {BUTTON.CLEAR_FILTERS}
                  </Button>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Group By Controls */}
      {showGroupBy && (
        <div className="d-block align-items-center justify-content-start gap-3 w-100">
          <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
            <DropDown dropdownMenu={groupArray} value={groupBy} setValue={handleGroupByChange} />
            {groupArray.includes(groupBy) && (
              <InputWithSearch
                setAvailableItem={setAvailableValuesGroupBy}
                availableItem={availableValuesGroupBy}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
              />
            )}
            {groupArray.includes(groupBy) && (
              <Button
                clickHandler={removeGroupBy}
                className="blue-btn d-flex align-items-center justify-content-center px-3">
                {BUTTON.REMOVE}
              </Button>
            )}
          </div>
        </div>
      )}

      {(showInlineSearch && fullProductData
        ? Array.isArray(fullProductData) && fullProductData.length > 0
        : Array.isArray(paginatedData) && paginatedData.length > 0) && (
        <div
          className={`pagination-container d-flex ${showInlineSearch ? '' : 'justify-content-between'} align-items-center mb-2`}>
          <h5 className="pagination-info">
            <span className="highlight">Records:</span>
            <span className="record-range">
              {paginationData.start} - {paginationData.end}
            </span>
            <span className="highlight">of</span>
            <span className="total-records">{paginationData.total_records}</span>
          </h5>

          <div
            className={`pagination-controls d-flex align-items-center justify-content-end ${showInlineSearch ? 'ms-auto' : ''}`}>
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${paginationData.page === 1 ? 'disabled' : ''}`}
              clickHandler={async () => {
                const newPage = Math.max(paginationData.page - 1, 1);
                await setPageNumber(newPage);
              }}
              disabled={paginationData.page === 1}>
              Previous
            </Button>

            <div className="page-jump-container d-flex align-items-center my-2">
              <input
                type="number"
                className="page-jump-input form-control me-2"
                placeholder={`On page ${paginationData.page} of ${Math.ceil(paginationData.total_records / PAGE_SIZE)}`}
                value={jumpPage}
                disabled={Math.ceil(paginationData.total_records / PAGE_SIZE) === 1}
                readOnly={Math.ceil(paginationData.total_records / PAGE_SIZE) === 1}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === '' ||
                    (/^[0-9]+$/.test(value) &&
                      value > 0 &&
                      value <= Math.ceil(paginationData.total_records / PAGE_SIZE))
                  ) {
                    setJumpPage(value);
                  } else {
                    setJumpPage('');
                  }
                }}
              />
              <Button
                className={`blue-btn ${
                  Math.ceil(paginationData.total_records / PAGE_SIZE) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                    ? 'disabled'
                    : ''
                } px-3`}
                clickHandler={() => {
                  handleJumpToPage();
                  setJumpPage('');
                }}
                disabled={
                  Math.ceil(paginationData.total_records / PAGE_SIZE) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                }>
                Go
              </Button>
            </div>

            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 mx-1 ${
                paginationData.page === Math.ceil(paginationData.total_records / PAGE_SIZE)
                  ? 'disabled'
                  : ''
              }`}
              clickHandler={async () => {
                const newPage = Math.min(
                  paginationData.page + 1,
                  Math.ceil(paginationData.total_records / PAGE_SIZE),
                );
                await setPageNumber(newPage);
              }}
              disabled={
                paginationData.page === Math.ceil(paginationData.total_records / PAGE_SIZE)
              }>
              Next
            </Button>
          </div>
        </div>
      )}

      {showRightTable ? (
        <div className="main_table_split_new">
          {/* LEFT SIDE */}
          <div className={`table-container ${className} position-relative mb-3 table-left-split`}>
            {isLoading ? (
              <div className="text-center my-5">
                <span className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">Loading...</span>
              </div>
            ) : paginatedData.length > 0 ? (
              <table className="w-100">
                <TableHead
                  columns={columns}
                  showColumn={finalColumns}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={setSelectedColumns}
                  colFilter={colFilter}
                  sortInfo={sortInfo}
                  setSortInfo={setSortInfo}
                />
                <TableBody
                  columns={finalColumns}
                  data={paginatedData}
                  colFilter={colFilter}
                  to={to !== '' ? to : undefined}
                  compiler={compiler}
                  sortInfo={sortInfo}
                  popUp={popUp}
                  setPopUp={setPopUp}
                  setPopUpData={setPopUpData}
                  setReload={setReload}
                  handleSort={handleSort}
                  func={func}
                  tooltip={tooltip}
                />
              </table>
            ) : (
              <h4 className="text-center px-1 py-5">No Records Found</h4>
            )}
          </div>

          <RightSplitTable
            fullProductData={fullProductData}
            rightTableLoading={rightTableLoading}
            setUpdatedMoves={setUpdatedMoves}
            rightTableColumn={rightTableColumn}
            tooltip={tooltip}
          />
        </div>
      ) : (
        // NOT SPLIT VIEW
        <div className={`table-container ${className} position-relative mb-3`}>
          {isLoading ? (
            <div className="text-center my-5">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          ) : paginatedData.length > 0 ? (
            <table className="w-100">
              <TableHead
                columns={columns}
                showColumn={finalColumns}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                colFilter={colFilter}
                sortInfo={sortInfo}
                setSortInfo={setSortInfo}
              />
              <TableBody
                columns={finalColumns}
                data={paginatedData}
                colFilter={colFilter}
                to={to !== '' ? to : undefined}
                compiler={compiler}
                sortInfo={sortInfo}
                popUp={popUp}
                setPopUp={setPopUp}
                setPopUpData={setPopUpData}
                setReload={setReload}
                handleSort={handleSort}
                func={func}
                tooltip={tooltip}
                showCollapseTable={showCollapseTable}
                collapseTableDataFunction={collapseTableDataFunction}
              />
            </table>
          ) : (
            <h4 className="text-center px-1 py-5">No Records Found</h4>
          )}
        </div>
      )}
    </>
  );
};
